/**
 * Country flag helper — maps football-country names to flag emoji.
 *
 * We use the 2-letter ISO country code → regional-indicator emoji trick
 * (each ASCII letter A-Z maps to 🇦-🇿 at codepoint 0x1F1E6 + (c - 'A')).
 *
 * Covers the vast majority of leagues NDAKUBET surfaces. Unknown country
 * returns 🏳️ (white flag) so rendering never breaks.
 */

const NAME_TO_ISO = {
  england: "GB",
  "great britain": "GB",
  "united kingdom": "GB",
  scotland: "GB",
  wales: "GB",
  "northern ireland": "GB",
  ireland: "IE",
  spain: "ES",
  italy: "IT",
  germany: "DE",
  france: "FR",
  portugal: "PT",
  netherlands: "NL",
  holland: "NL",
  belgium: "BE",
  luxembourg: "LU",
  switzerland: "CH",
  austria: "AT",
  poland: "PL",
  "czech republic": "CZ",
  czechia: "CZ",
  slovakia: "SK",
  hungary: "HU",
  romania: "RO",
  bulgaria: "BG",
  greece: "GR",
  turkey: "TR",
  turkiye: "TR",
  russia: "RU",
  ukraine: "UA",
  belarus: "BY",
  serbia: "RS",
  croatia: "HR",
  slovenia: "SI",
  "bosnia and herzegovina": "BA",
  bosnia: "BA",
  montenegro: "ME",
  "north macedonia": "MK",
  macedonia: "MK",
  albania: "AL",
  kosovo: "XK",
  moldova: "MD",
  estonia: "EE",
  latvia: "LV",
  lithuania: "LT",
  finland: "FI",
  sweden: "SE",
  norway: "NO",
  denmark: "DK",
  iceland: "IS",
  cyprus: "CY",
  malta: "MT",
  israel: "IL",

  brazil: "BR",
  argentina: "AR",
  uruguay: "UY",
  chile: "CL",
  paraguay: "PY",
  peru: "PE",
  colombia: "CO",
  ecuador: "EC",
  venezuela: "VE",
  bolivia: "BO",
  mexico: "MX",
  "costa rica": "CR",
  panama: "PA",
  honduras: "HN",
  guatemala: "GT",
  "el salvador": "SV",
  nicaragua: "NI",
  jamaica: "JM",
  "dominican republic": "DO",
  cuba: "CU",
  "trinidad and tobago": "TT",
  haiti: "HT",

  usa: "US",
  "united states": "US",
  "united-states": "US",
  "u.s.a.": "US",
  america: "US",
  canada: "CA",

  japan: "JP",
  "south korea": "KR",
  korea: "KR",
  "korea republic": "KR",
  "north korea": "KP",
  china: "CN",
  "china pr": "CN",
  "hong kong": "HK",
  taiwan: "TW",
  thailand: "TH",
  vietnam: "VN",
  indonesia: "ID",
  malaysia: "MY",
  singapore: "SG",
  philippines: "PH",
  india: "IN",
  pakistan: "PK",
  bangladesh: "BD",
  "sri lanka": "LK",
  iran: "IR",
  iraq: "IQ",
  "saudi arabia": "SA",
  "united arab emirates": "AE",
  uae: "AE",
  qatar: "QA",
  bahrain: "BH",
  kuwait: "KW",
  oman: "OM",
  yemen: "YE",
  jordan: "JO",
  lebanon: "LB",
  syria: "SY",
  palestine: "PS",

  egypt: "EG",
  morocco: "MA",
  algeria: "DZ",
  tunisia: "TN",
  libya: "LY",
  sudan: "SD",
  nigeria: "NG",
  ghana: "GH",
  "ivory coast": "CI",
  "cote d'ivoire": "CI",
  senegal: "SN",
  cameroon: "CM",
  "south africa": "ZA",
  kenya: "KE",
  uganda: "UG",
  tanzania: "TZ",
  ethiopia: "ET",
  rwanda: "RW",
  zambia: "ZM",
  zimbabwe: "ZW",
  angola: "AO",
  mozambique: "MZ",
  mali: "ML",
  "burkina faso": "BF",

  australia: "AU",
  "new zealand": "NZ",
  fiji: "FJ",

  international: "UN",
  world: "UN",
  europe: "EU",
};

const isoToEmoji = (iso) => {
  if (!iso) return "🏳️";
  const up = iso.toUpperCase();
  if (up === "XK") return "🇽🇰"; // Kosovo
  if (up === "UN") return "🌍"; // World / international
  if (up === "EU") return "🇪🇺";
  if (up.length !== 2) return "🏳️";
  const BASE = 0x1f1e6; // regional indicator A
  return String.fromCodePoint(BASE + (up.charCodeAt(0) - 65)) + String.fromCodePoint(BASE + (up.charCodeAt(1) - 65));
};

/** Convert a country name to a flag emoji. Returns '🏳️' when unknown. */
export const countryToFlag = (name) => {
  if (!name) return "🏳️";
  const key = String(name).toLowerCase().trim();
  const iso = NAME_TO_ISO[key];
  return isoToEmoji(iso);
};

/**
 * Small inline component — `🇬🇧 Premier League`.
 * Props:
 *   country — country name (string)
 *   league  — optional league name to render after the flag
 *   className — passthrough
 */
export const LeagueFlag = ({ country, league, className = "" }) => {
  const flag = countryToFlag(country);
  return (
    <span className={`inline-flex items-center gap-1 ${className}`} data-testid="league-flag">
      <span aria-hidden className="leading-none" title={country || ""}>
        {flag}
      </span>
      {league && <span className="truncate">{league}</span>}
    </span>
  );
};

export default LeagueFlag;
